import { useCallback, useContext } from 'react';
import { CustomerDesignResponse } from '@vp/upload-and-print-validator';
import { UploadContext, UploadContextProps } from 'src/providers/UploadContextProvider';
import { processAndValidateUpload, Upload } from 'src/services/UploadAndPrintService';
import { noticeError } from 'src/utils/newRelicUtil';
import { createTrackingDataForProduct } from 'src/utils/trackingUtil';
import { hasValidationErrors } from 'src/utils/validationUtil';
import { useErrorContext } from './ErrorContextHooks';
import { useProductContext } from './ProductContextHooks';
import { useStudioContext } from './StudioContextHooks';
import { useTrackingContext } from './TrackingContextHooks';
import { PricingContext, usePricingContext } from './usePricingContext';
import { useValidationContext } from './ValidationContextHooks';

export interface ProcessUpload {
  processUpload: (
    uploadedFile: Upload
  ) => Promise<{ newUpload: Upload; processUploadResponse: CustomerDesignResponse } | null>;
  reprocessUpload: () => Promise<{ newUpload: Upload; processUploadResponse: CustomerDesignResponse } | null>;
}

export function useUploadContext(): UploadContextProps {
  return useContext(UploadContext);
}

export function useProcessUpload(): ProcessUpload {
  const { upload, setUpload } = useUploadContext();
  const { setValidations, setIsValidating } = useValidationContext();
  const { studioUrl } = useStudioContext();
  const { setError } = useErrorContext();
  const { productInformation, setProductInformation } = useProductContext();
  const { trackingConfiguration } = useTrackingContext();
  const trackingDataForProduct = createTrackingDataForProduct(productInformation);
  const pricingContext = usePricingContext() || ({} as PricingContext);

  const processUpload = useCallback(
    async (
      uploadedFile: Upload
    ): Promise<{ newUpload: Upload; processUploadResponse: CustomerDesignResponse } | null> => {
      if (!uploadedFile) {
        return null;
      }

      setValidations(null);
      setIsValidating(true);

      try {
        const [processedUploadUri, processUploadResponse] = await processAndValidateUpload(
          uploadedFile.fileUri,
          productInformation,
          trackingConfiguration,
          trackingDataForProduct,
          pricingContext.vatInclusive
        );

        // Show previews only if there are no errors in validations or the escape hatch to studio is available
        const renderingInstructionsUrl =
          studioUrl || !hasValidationErrors(processUploadResponse.validations)
            ? processUploadResponse.renderingInstructionsUrl
            : undefined;

        const newUpload = { ...uploadedFile, processedUploadUri, renderingInstructionsUrl };

        setUpload(newUpload);
        setValidations(processUploadResponse.validations);
        if (processUploadResponse.productData.productOptions) {
          setProductInformation({
            ...productInformation,
            recommendedProductData: processUploadResponse.productData.price && {
              price: processUploadResponse.productData.price,
              productOptions: processUploadResponse.productData.productOptions,
            },
          });
        }

        return { newUpload, processUploadResponse };
      } catch (e) {
        console.error('Error processing file', e);
        noticeError(e, {
          method: 'processUpload',
          uploadedFile: uploadedFile,
        });
        setError(e);
        return null;
      } finally {
        setIsValidating(false);
      }
    },
    [
      pricingContext.vatInclusive,
      productInformation,
      setError,
      setIsValidating,
      setProductInformation,
      setUpload,
      setValidations,
      studioUrl,
      trackingConfiguration,
      trackingDataForProduct,
    ]
  );

  const reprocessUpload = useCallback(async () => {
    if (!upload) {
      return null;
    }

    const newUpload: Upload = {
      ...upload,
      processedUploadUri: '',
      renderingInstructionsUrl: '',
    };
    setUpload(newUpload);
    return await processUpload(newUpload);
  }, [upload, setUpload, processUpload]);

  return { processUpload, reprocessUpload };
}
